import config, { getLocaleConfig } from 'config';
import { Entry } from 'contentful';
import {
  IBlogCategoryFields,
  IBlogPostFields,
  IProductPagesFields,
} from 'global';
import useWrappedSWR from 'hooks/common/use-wrapped-swr';
import { getMagazineBathPath } from 'modules/magazine/constants';
import MagazineHome from 'modules/magazine/magazine-home';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { useCallback } from 'react';
import {
  contentfulClient,
  fetchContentfulByHandle,
  fetchContentfulEntries,
} from '../../connectors/contentful';

export interface MagazinePageProps {
  articles: Entry<IBlogPostFields>[];
  inFocusArticle: null | Entry<IBlogPostFields>;
  mainCategories: Array<IBlogCategoryFields>;
}

const IN_FOCUS_HANDLE = 'magazine-in-focus';

const fetchInFocus = async ({
  locale,
}: {
  locale?: string;
}): Promise<Entry<IBlogPostFields> | null> => {
  const inFocusPageEntry = await fetchContentfulByHandle<IProductPagesFields>({
    handle: IN_FOCUS_HANDLE,
    locale,
    query: {
      content_type: 'productPages',
      // fetch nested references up to 2 levels deep
      include: 2,
    },
  });

  if (!inFocusPageEntry) return null;

  const _inFocus: unknown = inFocusPageEntry.fields.header?.filter(
    (v) => v.sys.contentType.sys.id === 'blogPost'
  );

  // _inFocus could be Entry<{ [fieldId: string]: unknown }>[] | undefined
  if (_inFocus) {
    return (_inFocus as Array<Entry<IBlogPostFields>>)[0];
  }

  return null;
};

const defaultQuery = {
  content_type: 'blogPost',
  order: '-fields.publishDate',
  include: 2,
  'fields.published': null,
};

const fetchBlogEntries = async ({
  inFocusEntry,
  query,
}: {
  inFocusEntry: Entry<IBlogPostFields> | null;
  query: any;
}): Promise<MagazinePageProps> => {
  const limit = inFocusEntry ? MAIN_PAGE_BLOG_LIMIT + 1 : MAIN_PAGE_BLOG_LIMIT;

  const blogEntries = await fetchContentfulEntries<IBlogPostFields>({
    ...query,
    limit,
  });

  let articles = blogEntries.items;
  // Omit article in focus section
  if (inFocusEntry) {
    articles = articles.filter((item) => item.sys.id !== inFocusEntry.sys.id);
    if (articles.length > MAIN_PAGE_BLOG_LIMIT) {
      articles.pop();
    }
  }

  const blogMainCategories =
    await contentfulClient.getEntries<IBlogCategoryFields>({
      'fields.isMainCategory': true,
      content_type: 'blogCategory',
    });

  return {
    articles,
    inFocusArticle: inFocusEntry || null,
    mainCategories: blogMainCategories.items.map((item) => item.fields),
  };
};

const MAIN_PAGE_BLOG_LIMIT = 6;

const MagazinePage: NextPage<MagazinePageProps> = ({
  articles,
  inFocusArticle,
  mainCategories,
}) => {
  const { locale } = useRouter();
  const { data: inFocus } = useWrappedSWR<Entry<IBlogPostFields> | null>({
    query: 'fetchInFocus',
    fetchFn: () => {
      return fetchInFocus({ locale });
    },
    fallbackData: inFocusArticle,
  });

  const _fetchBlogEntries = useCallback(() => {
    return fetchBlogEntries({
      inFocusEntry: inFocus || null,
      query: defaultQuery,
    });
  }, [inFocus]);

  const { data } = useWrappedSWR<MagazinePageProps>({
    query: defaultQuery,
    fetchFn: _fetchBlogEntries,
    fallbackData: {
      articles,
      inFocusArticle,
      mainCategories,
    },
  });

  if (!data) {
    return <div>Not Found!</div>;
  }

  return (
    <MagazineHome
      articles={data.articles}
      inFocusArticle={data.inFocusArticle || null}
      mainCategories={data.mainCategories}
    />
  );
};

interface UrlQuery extends ParsedUrlQuery, BathUrlQuery {}

export interface BathUrlQuery {
  magazine: string;
}

export const getStaticProps: GetStaticProps<
  MagazinePageProps,
  UrlQuery
> = async (context) => {
  const { shopId } = getLocaleConfig(context.locale);
  const magazineBathPath = getMagazineBathPath(shopId);

  if (context.params?.magazine !== magazineBathPath) {
    return { notFound: true, revalidate: true };
  }

  let inFocusEntry: Entry<IBlogPostFields> | null = null;

  try {
    inFocusEntry = await fetchInFocus({ locale: context.locale });
  } catch (error) {
    console.error(`Error while inFocusArticle`, error);
  }

  try {
    const props = await fetchBlogEntries({
      inFocusEntry,
      query: defaultQuery,
    });

    return {
      props,
      revalidate: 3600, // 1 hour
    };
  } catch (error) {
    console.error(`Error while prefetching data in magazine`, error);
    return { notFound: true, revalidate: true };
  }
};

export const getStaticPaths: GetStaticPaths<UrlQuery> = async () => {
  // Get paths for all supported locales
  const paths = config.magazineSitemapLocales.map((locale) => {
    const { shopId } = getLocaleConfig(locale);
    const magazinePath = getMagazineBathPath(shopId);

    return {
      params: { magazine: magazinePath },
      locale,
    };
  });

  return {
    paths,
    fallback: false,
  };
};

export default MagazinePage;
